import { useTranslation } from 'react-i18next';
import { WindowIcon } from '@heroicons/react/24/outline';
import type { tEmbeddedSignupErrorReport } from 'src/hooks/whatsapp/whatsappConnect.types';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { formatMetaReportedMessage } from 'src/components/WhatsAppConnect/WhatsAppConnect.functions';
import {
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import { findWizardErrorCatalogEntry } from './WhatsAppConnectionWizard.functions';
import WhatsAppConnectionWizardErrorSteps from './WhatsAppConnectionWizardErrorSteps.component';
import { WhatsAppConnectionWizardAdvisorRow } from './WhatsAppConnectionWizardExternalLink.component';

export type tWhatsAppConnectionWizardReportedErrorProps = {
  errorReport: tEmbeddedSignupErrorReport;
  isMetaWindowOpen: boolean;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * The main pane after the person reports an error from Meta's window: the
 * error is the title and its cause the subtitle. While the window is open the
 * footer is empty and a note asks to close it; once closed, the footer
 * carries the error's fix.
 *
 * @param {tWhatsAppConnectionWizardReportedErrorProps} props - Meta's report
 * and whether its window is still open.
 * @returns {JSX.Element} The reported error screen.
 */
export default function WhatsAppConnectionWizardReportedError(
  props: tWhatsAppConnectionWizardReportedErrorProps,
) {
  const { errorReport, isMetaWindowOpen, onAdvisorCtaClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const entry = findWizardErrorCatalogEntry(errorReport.code);
  // Meta's window never labels the code, so it only helps an advisor find
  // the case.
  const codeLine =
    errorReport.code === null
      ? undefined
      : t('wizard.reportedError.metaCodeLine', { code: errorReport.code });

  function renderKnownError() {
    if (!entry) return null;
    // Once the window closes, an error only an advisor or Meta can solve
    // takes the footer's primary, so the body leaves it out.
    const footerTakesFix = !isMetaWindowOpen && entry.footerPrimary !== 'retry';
    const footerLeadsWithAdvisor =
      footerTakesFix && entry.footerPrimary === 'advisor';

    return (
      <section className="flex flex-col gap-[var(--ls-spacing-4)]">
        <WhatsAppConnectionWizardErrorSteps
          entry={entry}
          isMetaWindowOpen={isMetaWindowOpen}
          showsMetaLink={!footerTakesFix}
          supportLine={codeLine}
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
        {!footerLeadsWithAdvisor && (
          <WhatsAppConnectionWizardAdvisorRow
            onAdvisorCtaClick={onAdvisorCtaClick}
          />
        )}
      </section>
    );
  }

  function renderUnknownError() {
    const metaMessage = formatMetaReportedMessage(errorReport.message);
    return (
      <section className="flex flex-col gap-[var(--ls-spacing-4)]">
        {metaMessage && (
          <div className="flex flex-col gap-[var(--ls-spacing-1)]">
            <p className={WIZARD_CAPTION_CLASS_NAME}>
              {t('wizard.reportedError.metaShowedLabel')}
            </p>
            {/* Meta's own words, quoted: it sends them already in Spanish. */}
            <p className="text-[length:var(--ls-body-lg)] text-[var(--ls-text-neutral-default)]">
              {`"${metaMessage}"`}
            </p>
          </div>
        )}
        {codeLine && <p className={WIZARD_CAPTION_CLASS_NAME}>{codeLine}</p>}
        <WhatsAppConnectionWizardAdvisorRow
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {entry
            ? t(`wizard.errorCatalog.${entry.id}.title`)
            : t('wizard.reportedError.unknownTitle')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>
          {entry
            ? t(`wizard.errorCatalog.${entry.id}.cause`)
            : t('wizard.reportedError.unknownSubtitle')}
        </p>
      </div>
      {entry ? renderKnownError() : renderUnknownError()}
      {/* Mounted with the screen and emptied when the window closes, so a
          screen reader announces the change of its text. */}
      <div role="status" className="flex gap-[var(--ls-spacing-2)]">
        {isMetaWindowOpen && (
          <>
            <WindowIcon className={WIZARD_ICON_CLASS_NAME} aria-hidden="true" />
            <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
              {t('wizard.reportedError.windowOpenNotice')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
